import { useState, useEffect } from "react";
import { ClipboardList, Droplets, Loader2, Clock, Waves } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAM_STYLES = {
  PARRALILLO: { accent: "#22c55e", bg: "bg-emerald-900/20", border: "border-emerald-500/30", text: "text-emerald-400" },
  SIBERIO: { accent: "#eab308", bg: "bg-yellow-900/20", border: "border-yellow-500/30", text: "text-yellow-400" },
  CAIDERO: { accent: "#3b82f6", bg: "bg-blue-900/20", border: "border-blue-500/30", text: "text-blue-400" },
  OTROS: { accent: "#a1a1aa", bg: "bg-zinc-800/40", border: "border-zinc-600/40", text: "text-zinc-400" },
};

const DamCard = ({ dam, data }) => {
  const style = DAM_STYLES[dam] || DAM_STYLES.OTROS;
  return (
    <Card className={`${style.bg} ${style.border} p-3 text-center`} data-testid={`daily-report-dam-${dam}`}>
      <span className={`text-[11px] uppercase font-bold tracking-wider ${style.text}`}>{dam}</span>
      <div className="text-2xl font-bold font-mono text-amber-300 mt-1" data-testid={`daily-report-azadas-${dam}`}>
        {data.azadas.toFixed(2)} <span className="text-sm text-amber-500">azd</span>
      </div>
      <div className="text-xs text-zinc-400 mt-1">
        {data.volume_m3.toLocaleString("es-ES")} m³
      </div>
      <div className="text-[10px] text-zinc-500 mt-0.5">
        {data.hours} h · {data.operations} {data.operations === 1 ? "registro" : "registros"}
      </div>
    </Card>
  );
};

export const DailyReportButton = ({ onClick }) => (
  <Button
    variant="ghost"
    size="icon"
    onClick={onClick}
    className="text-zinc-400 hover:text-amber-400 h-8 w-8 md:h-10 md:w-10 p-0"
    data-testid="daily-report-btn"
    title="Parte Diario de Riego"
  >
    <ClipboardList className="w-5 h-5 md:w-6 md:h-6" />
  </Button>
);

const DailyReport = ({ open: controlledOpen, onOpenChange }) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  // Auto-abrir una vez cada mañana
  useEffect(() => {
    const todayKey = new Date().toLocaleDateString("es-ES", { timeZone: "Atlantic/Canary" });
    const lastShown = localStorage.getItem("centinela_daily_report_date");
    if (lastShown !== todayKey) {
      setOpen(true);
      localStorage.setItem("centinela_daily_report_date", todayKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/irrigation/daily-report`);
      setReport(res.data);
    } catch (e) {
      console.error("Error fetching daily report:", e);
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const dams = ["PARRALILLO", "SIBERIO", "CAIDERO"];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl w-[95vw] max-h-[92vh] overflow-y-auto" data-testid="daily-report-dialog">
        <DialogHeader className="pb-1">
          <DialogTitle className="font-heading uppercase tracking-wider text-zinc-100 flex items-center gap-2 text-sm">
            <ClipboardList className="w-4 h-4 text-amber-400" />
            Parte Diario de Riego
            {report?.date && (
              <span className="text-zinc-400 font-mono text-xs normal-case tracking-normal">
                — {formatDate(report.date)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Resumen de azadas y metros cúbicos de agua echados el día anterior, agrupado por presa.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-7 h-7 animate-spin text-amber-400" />
          </div>
        ) : report ? (
          <div className="space-y-4 py-1">
            {report.total.operations === 0 ? (
              <div className="text-center py-8 bg-zinc-800/30 rounded-lg border border-zinc-700/50">
                <Waves className="w-9 h-9 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-400 text-sm">No se registraron vaciados el {formatDate(report.date)}</p>
              </div>
            ) : (
              <>
                {/* Tarjetas por presa */}
                <div className="grid grid-cols-3 gap-3">
                  {dams.map((dam) => (
                    <DamCard key={dam} dam={dam} data={report.by_dam[dam] || { azadas: 0, volume_m3: 0, hours: 0, operations: 0 }} />
                  ))}
                </div>

                {/* Total */}
                <Card className="bg-gradient-to-r from-amber-900/20 to-blue-900/20 border-amber-500/30 p-3">
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div>
                      <div className="text-[10px] text-zinc-500 uppercase">Total Azadas</div>
                      <div className="text-xl font-bold font-mono text-amber-400" data-testid="daily-report-total-azadas">
                        {report.total.azadas.toFixed(2)}
                      </div>
                    </div>
                    <div className="border-x border-zinc-700">
                      <div className="text-[10px] text-zinc-500 uppercase">Total m³</div>
                      <div className="text-xl font-bold font-mono text-blue-400">
                        {report.total.volume_m3.toLocaleString("es-ES")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[10px] text-zinc-500 uppercase">Total Tiempo</div>
                      <div className="text-xl font-bold font-mono text-white">
                        {report.total.hours} <span className="text-xs text-zinc-400">h</span>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* Detalle de registros */}
                {report.details.length > 0 && (
                  <div>
                    <h4 className="text-[10px] uppercase text-zinc-400 font-bold mb-1.5 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      Detalle de Vaciados
                    </h4>
                    <div className="space-y-1 max-h-56 overflow-y-auto pr-1">
                      {report.details.map((op, idx) => {
                        const style = DAM_STYLES[op.dam] || DAM_STYLES.OTROS;
                        const time = op.closed_at
                          ? new Date(op.closed_at).toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit", timeZone: "Atlantic/Canary" })
                          : "";
                        return (
                          <div key={idx} className="flex items-center justify-between bg-zinc-800/50 rounded px-2 py-1.5 text-[11px]">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: style.accent }} />
                              <span className="font-bold text-zinc-200 truncate">{op.control_point_name || op.dam}</span>
                              {op.operator && <span className="text-zinc-500 truncate hidden sm:inline">· {op.operator}</span>}
                              {time && <span className="text-zinc-600 font-mono">{time}</span>}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0 font-mono">
                              <span className="text-zinc-400">{op.hours_water} h</span>
                              <span className="text-amber-400 font-bold">{op.azadas.toFixed(2)} azd</span>
                              <span className="text-cyan-400">{op.volume_m3.toLocaleString("es-ES")} m³</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
              <span className="text-[10px] text-zinc-500 flex items-center gap-1">
                <Droplets className="w-3 h-3 text-blue-400" /> 1 azada = 432 m³ (10 l/s · 12 h)
              </span>
              <Button
                onClick={() => setOpen(false)}
                className="bg-amber-600 hover:bg-amber-700 h-8 px-4 text-xs"
                data-testid="daily-report-close-btn"
              >
                Entendido
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-zinc-500 text-sm">No se pudo cargar el parte diario.</div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DailyReport;
